<template>
  <div class="page stream-page">
    <div class="container">
      <div class="stream-page__container">
        <div class="stream-page__video">
          <button
            @click="joinBroadcast"
            v-if="!isStreamStart && !streamLoading"
            class="btn btn--lg btn--green stream-page__btn"
          >
            Смотреть
          </button>
          <button v-if="streamLoading && !isStreamStart" class="btn btn--lg btn--green stream-page__btn">
            <LoadingIndicator title="Загрузка" />
          </button>
          <video
            v-show="isStreamStart"
            id="video"
            width="640"
            height="800"
            ref="viewer"
            autoplay
            controls
          ></video>
        </div>
        <div v-if="isStreamStart" class="stream-page__chat-wrapper">
          <div class="stream-page__chat-header">
            <h3>Чат</h3>
            <div class="stream-page__chat-live">
              <div class="stream-page__chat-head">LIVE</div>
              <div class="stream-page__chat-count">{{ streamingUsers.length }}</div>
            </div>
          </div>
          <ul class="stream-page__chat">
            <li v-for="(item, i) in chat" :key="i">
              <span style="font-weight: 500; color: orange">{{ item.name || "Guest" }}:</span>
              {{ item.comment }}
            </li>
          </ul>
          <div class="stream-page__input">
            <InputComponent v-model="message" placeholder="Ваш комментарий" @enter="sendMessage" />
            <div @click="sendMessage">
              <RemixIconComponent class="stream-page__input-svg" category="Business" name="send-plane-line" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Peer from "simple-peer";
import axios from "axios";
import CREATE_COMMENT from "@/graphql/mutations/CreateComment.graphql";
import LoadingIndicator from "components/LoadingIndicator.vue";
import InputComponent from "components/inputs/InputComponent.vue";
import RemixIconComponent from "components/RemixIconComponent.vue";
import { getCookie } from "../../utils.js";

export default {
  name: "StreamPage",
  components: { RemixIconComponent, InputComponent, LoadingIndicator },

  data() {
    return {
      isStreamStart: false,
      streamingPresenceChannel: null,
      broadcasterPeer: null,
      broadcasterId: null,
      streamLoading: false,
      message: null,
      chat: [],
      streamingUsers: [],
      iceServers: [],
      iceCredentialsExpiresAt: 0,
    };
  },

  mounted() {
    this.play();

    // Debug authentication token
    console.log("[DEBUG] Authentication check on mount:");
    console.log("[DEBUG] Apollo token:", this.$store.state.apollo_token ? "Present" : "Missing");
    console.log(
      "[DEBUG] Auth user:",
      this.$store.state.auth && this.$store.state.auth.user ? "Present" : "Missing"
    );
    console.log("[DEBUG] CSRF token:", getCookie("XSRF-TOKEN") ? "Present" : "Missing");

    // Check if Echo is properly initialized with auth token
    if (window.Echo && window.Echo.connector && window.Echo.connector.pusher) {
      console.log("[DEBUG] Echo connection state:", window.Echo.connector.pusher.connection.state);
    } else {
      console.log("[DEBUG] Echo not properly initialized");
    }

    // Watch for auth user changes to reinitialize channels if needed
    this.$store.watch(
      (state) => state.auth.user,
      (newUser, oldUser) => {
        if ((!oldUser && newUser) || (oldUser && newUser && oldUser.id !== newUser.id)) {
          console.log("[DEBUG] User changed, reinitializing channels if needed");
          // Only reinitialize if we're already in streaming mode
          if (this.streamLoading || this.isStreamStart) {
            this.initializeChannels();
          }
        }
      }
    );
  },
  beforeDestroy() {
    location.reload();
    this.removeBroadcastVideo();
    if (this.broadcasterPeer) {
      this.broadcasterPeer.destroy();
    }
  },

  methods: {
    async loadIceServers(force = false) {
      const refreshBeforeMs = 60 * 1000;

      if (
        !force &&
        Array.isArray(this.iceServers) &&
        this.iceServers.length &&
        Date.now() + refreshBeforeMs < this.iceCredentialsExpiresAt
      ) {
        return this.iceServers;
      }

      if (!this.$store.state.apollo_token) {
        throw new Error("Authentication token required for ICE credentials");
      }

      const mediaEndpoint = (this.$store.state._env.MEDIA_ENDPOINT || "").replace(/\/$/, "");
      if (!mediaEndpoint) {
        throw new Error("MEDIA_ENDPOINT is not configured");
      }

      const response = await axios.get(`${mediaEndpoint}/api/media/ice`, {
        headers: {
          Authorization: "Bearer " + this.$store.state.apollo_token,
        },
      });

      const payload = response.data && response.data.data ? response.data.data : response.data;
      if (!payload || !Array.isArray(payload.iceServers) || !payload.iceServers.length) {
        throw new Error("Invalid ICE configuration received from backend");
      }

      const ttl = Number(payload.ttl) > 0 ? Number(payload.ttl) : 600;
      this.iceServers = payload.iceServers;
      this.iceCredentialsExpiresAt = Date.now() + ttl * 1000;

      console.log("[DEBUG] Dynamic ICE configuration loaded:", {
        servers: this.iceServers.length,
        ttl,
      });

      return this.iceServers;
    },

    // Check if user is authenticated and prompt login if needed
    checkAuthentication() {
      console.log("[DEBUG] Checking authentication status");
      if (!this.$store.state.apollo_token || !this.$store.state.auth || !this.$store.state.auth.user) {
        console.log("[DEBUG] User not authenticated, showing login modal");
        this.$store.state._modals.push({
          component: () => import("components/modals/components/LoginModal.vue"),
          props: {
            onSuccess: () => {
              console.log("[DEBUG] Login successful, reinitializing Echo");
              // Reinitialize Echo with new token
              if (typeof window.initializeEcho === 'function') {
                window.initializeEcho();
                // Wait a moment for Echo to connect before retrying
                setTimeout(() => {
                  if (this.streamLoading || this.isStreamStart) {
                    console.log("[DEBUG] Reinitializing channels after login");
                    this.initializeChannels();
                  }
                }, 1000);
              }
            }
          }
        });
        return false;
      }
      return true;
    },

    play() {
      this.isStreamStart = false;
      this.streamLoading = false;

      // При переходе с другой страницы (параметр autoplay) будет автоматически грузиться стрим
      // при перезагрузке же страницы будет показываться кнопка, после нажатия которой будет грузиться стрим
      // это нужно т.к. браузер запрещает воспроизводить пользователю звуки до его действий на сайте
      // нужно сделать загрузку видео отдельно, потом при нажатии кнопки включается звук, как на twitch
      try {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("autoplay")) {
          this.joinBroadcast();
          urlParams.delete("autoplay");
          history.replaceState(null, "", location.pathname + "?" + urlParams.toString());
        }
      } catch (error) {
        console.error("[DEBUG] Error in autoplay handling:", error);
      }
    },

    async joinBroadcast() {
      try {
        console.log("[DEBUG] joinBroadcast called - starting connection process");
        console.log("[DEBUG] Current state:", {
          isStreamStart: this.isStreamStart,
          streamLoading: this.streamLoading,
          authUserId: this.authUserId,
          link: this.link,
        });

        // Check authentication before proceeding
        if (!this.checkAuthentication()) {
          console.log("[DEBUG] Authentication check failed, waiting for login");
          return;
        }

        this.streamLoading = true;
        console.log("[DEBUG] Set streamLoading to true");

        // Use the centralized channel initialization method
        await this.initializeChannels();
      } catch (error) {
        console.error("[DEBUG] Error in joinBroadcast:", error);
        this.streamLoading = false;
      }
    },
    async initializeChannels() {
      console.log("[DEBUG] Initializing all channels");
      try {
        await this.loadIceServers();
        await this.initializeStreamingChannel();
        await this.initializeSignalOfferChannel();
        await this.initializeSignalIceCadndidateOfferChannel();
        console.log("[DEBUG] All channels initialized successfully");
      } catch (error) {
        console.error("[DEBUG] Error initializing channels:", error);

        const status =
          error && error.response && error.response.status
            ? error.response.status
            : error.status;

        const isAuthenticationError =
          status === 401 ||
          (error.message && error.message.includes("401"));

        // Check if this is an authentication error
        if (isAuthenticationError) {
          console.error("[DEBUG] Authentication error detected. Current auth state:", {
            token: this.$store.state.apollo_token ? "Present" : "Missing",
            user: this.$store.state.auth && this.$store.state.auth.user ? "Present" : "Missing",
            echoConnected: window.Echo && window.Echo.connector ? window.Echo.connector.pusher.connection.state : "Not initialized"
          });

          // If we have a token but still getting 401, try reinitializing Echo
          if (this.$store.state.apollo_token) {
            console.log("[DEBUG] Attempting to reinitialize Echo with current token");
            if (typeof window.initializeEcho === 'function') {
              window.initializeEcho();
              // Wait a moment for Echo to connect before retrying
              setTimeout(() => {
                if (this.streamLoading || this.isStreamStart) {
                  console.log("[DEBUG] Reinitializing channels after login");
                  this.initializeChannels();
                }
              }, 1000);
            }
          }
        } else {
          // Stop the loading state on authorization, network or other failures.
          this.streamLoading = false;
        }
      }
    },

    async initializeStreamingChannel() {
      console.log("[DEBUG] initializeStreamingChannel - Starting with link:", this.link);

      try {
        // Check if we have the required authentication
        if (!this.$store.state.apollo_token) {
          console.error("[DEBUG] Missing authentication token for streaming channel");
          throw new Error("Authentication token required");
        }

        // Log Echo connection state
        if (window.Echo && window.Echo.connector && window.Echo.connector.pusher) {
          console.log("[DEBUG] Echo connection state before subscribing:", window.Echo.connector.pusher.connection.state);
          console.log("[DEBUG] Echo socket ID:", window.Echo.socketId());
        } else {
          console.error("[DEBUG] Echo not properly initialized before channel subscription");
          if (typeof window.initializeEcho === 'function') {
            console.log("[DEBUG] Attempting to initialize Echo");
            window.initializeEcho();
            // Wait for connection before proceeding
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        console.log("[DEBUG] Subscribing to presence channel:", `streaming-channel.${this.link}`);
        this.streamingPresenceChannel = window.Echo.join(`streaming-channel.${this.link}`);

        console.log("[DEBUG] Adding event listeners to presence channel");
        this.streamingPresenceChannel.listen(
          "StreamCommentCreated",
          (data) => {
            console.log("[DEBUG] StreamCommentCreated event received:", data);
            let name = `Guest${data.comment.user.id}`;

          if (data.comment.user.name) {
            name = data.comment.user.name;
          } else if (data.comment.user.shop) {
            name = data.comment.user.name;
          }

          //chat
          const comment = data.comment.comment;
          const result = {
            name,
            comment,
          };

            this.chat = [...this.chat, result];
            console.log("[DEBUG] Chat updated, new length:", this.chat.length);
          }
        );

        this.streamingPresenceChannel.here((users) => {
          console.log("[DEBUG] Presence channel 'here' event - users:", users);
          this.streamingUsers = users;
        });

        this.streamingPresenceChannel.joining((user) => {
          console.log("[DEBUG] User joining:", user);
          // if this new user is not already on the call, send your stream offer
          const joiningUserIndex = this.streamingUsers.findIndex((data) => data.id === user.id);
          if (joiningUserIndex < 0) {
            this.streamingUsers.push(user);
            console.log("[DEBUG] Added new user to streamingUsers, new count:", this.streamingUsers.length);
          } else {
            console.log("[DEBUG] User already in streamingUsers list");
          }
        });

        this.streamingPresenceChannel.leaving((user) => {
          console.log("[DEBUG] User leaving:", user);
          const leavingUserIndex = this.streamingUsers.findIndex((data) => data.id === user.id);
          this.streamingUsers.splice(leavingUserIndex, 1);
          console.log("[DEBUG] Removed user from streamingUsers, new count:", this.streamingUsers.length);
        });

        console.log("[DEBUG] All presence channel event handlers set up successfully");
      } catch (error) {
        console.error("[DEBUG] Error in initializeStreamingChannel:", error);
        // Check if this is an authentication error
        if (error.status === 401 || (error.message && error.message.includes('401'))) {
          console.error("[DEBUG] Authentication error for streaming channel. Headers:", {
            Authorization: `Bearer ${this.$store.state.apollo_token ? this.$store.state.apollo_token.substring(0, 10) + "..." : "Missing"}`,
            "X-CSRF-TOKEN": getCookie("XSRF-TOKEN") ? "Present" : "Missing"
          });
        }
        throw error;
      }
    },

    async sendMessage() {
      await this.$apollo.mutate({
        mutation: CREATE_COMMENT,
        variables: {
          stream_id: parseInt(this.streamId),
          comment: this.message,
        },
        context: {
          headers: {
            Authorization: "Bearer " + this.$store.state.apollo_token,
          },
        },
      });
      this.message = null;
    },

    async createViewerPeer(incomingOffer, broadcaster) {
      console.log("[DEBUG] createViewerPeer called with:", {
        broadcaster,
        offerType: incomingOffer.type,
        offerSdp: incomingOffer.sdp ? incomingOffer.sdp.substring(0, 100) + "..." : "No SDP",
      });

      try {
        // Refresh short-lived credentials if they are close to expiry.
        await this.loadIceServers();
      } catch (error) {
        console.error("[DEBUG] Unable to load ICE configuration before peer creation:", error);
        this.streamLoading = false;
        return;
      }

      if (!Array.isArray(this.iceServers) || !this.iceServers.length) {
        console.error("[DEBUG] Cannot create viewer peer - ICE configuration is missing");
        return;
      }

      console.log("[DEBUG] Creating peer with dynamic ICE configuration:", {
        servers: this.iceServers.length,
      });

      const peer = new Peer({
        initiator: false,
        trickle: true,
        config: {
          iceServers: this.iceServers,
        },
      });
      console.log("[DEBUG] Peer object created:", peer);

      // Add Transceivers
      peer.addTransceiver("video", { direction: "recvonly" });
      peer.addTransceiver("audio", { direction: "recvonly" });
      console.log("[DEBUG] Added video and audio transceivers with recvonly direction");

      // Initialize Peer events for connection to remote peer
      this.handlePeerEvents(peer, incomingOffer, broadcaster, this.removeBroadcastVideo);
      console.log("[DEBUG] Peer event handlers initialized");

      this.broadcasterPeer = peer;
      console.log("[DEBUG] Set broadcasterPeer reference");
    },

    handlePeerEvents(peer, incomingOffer, broadcaster, cleanupCallback) {
      console.log("[DEBUG] handlePeerEvents called with broadcaster:", broadcaster);
      console.log("[DEBUG] incomingOffer:", incomingOffer);
      console.log("[DEBUG] peer object:", peer);

      // Monitor all peer connection state changes
      peer._pc.addEventListener("connectionstatechange", () => {
        console.log(`[DEBUG] Connection state changed: ${peer._pc.connectionState}`);
      });

      peer._pc.addEventListener("iceconnectionstatechange", () => {
        console.log(`[DEBUG] ICE connection state changed: ${peer._pc.iceConnectionState}`);
      });

      peer._pc.addEventListener("icegatheringstatechange", () => {
        console.log(`[DEBUG] ICE gathering state changed: ${peer._pc.iceGatheringState}`);
      });

      peer._pc.addEventListener("signalingstatechange", () => {
        console.log(`[DEBUG] Signaling state changed: ${peer._pc.signalingState}`);
      });

      peer.on("signal", (data) => {
        console.log("[DEBUG] Peer 'signal' event with data type:", data.type);

        if (data.type === "candidate") {
          console.log("[DEBUG] ICE candidate generated:", data.candidate);
          /** ОТПРАВЛЯЕМ КАНДИДАТОВ НА СЕРВЕР **/
          console.log("[DEBUG] Sending ICE candidate to server:", {
            endpoint: this.$store.state._env.MEDIA_ENDPOINT + "/action/stream-ice-candidate-answer",
            broadcaster: this.broadcasterId,
          });

          axios
            .post(
              this.$store.state._env.MEDIA_ENDPOINT + "/action/stream-ice-candidate-answer",
              {
                broadcaster: this.broadcasterId,
                candidate: data.candidate,
              },
              {
                headers: {
                  Authorization: "Bearer " + this.$store.state.apollo_token,
                  "Content-Type": "application/json",
                },
              }
            )
            .then((res) => {
              console.log("[DEBUG] ICE candidate sent successfully:", res.status);
            })
            .catch((err) => {
              console.error("[DEBUG] Error sending ICE candidate:", err);
              console.log("[DEBUG] Error details:", {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
              });
            });
        }

        if (data.type === "answer") {
          console.log("[DEBUG] SDP answer created:", data.sdp?.substring(0, 100) + "...");
          console.log("[DEBUG] Sending answer to server:", {
            endpoint: this.$store.state._env.MEDIA_ENDPOINT + "/action/stream-answer",
            broadcaster,
          });

          axios
            .post(
              this.$store.state._env.MEDIA_ENDPOINT + "/action/stream-answer",
              {
                broadcaster,
                answer: data.sdp,
              },
              {
                headers: {
                  Authorization: "Bearer " + this.$store.state.apollo_token,
                  "Content-Type": "application/json",
                },
              }
            )
            .then((res) => {
              console.log("[DEBUG] Answer sent successfully:", res.status);
            })
            .catch((err) => {
              console.error("[DEBUG] Error sending answer:", err);
              console.log("[DEBUG] Error details:", {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
              });
            });
        }
      });

      peer.on("stream", (stream) => {
        console.log("[DEBUG] Stream received:", stream);
        console.log(
          "[DEBUG] Stream tracks:",
          stream.getTracks().map((track) => ({
            kind: track.kind,
            enabled: track.enabled,
            readyState: track.readyState,
            id: track.id,
          }))
        );

        // display remote stream
        let video = this.$refs.viewer;
        console.log("[DEBUG] Video element:", video);

        if ("srcObject" in video) {
          console.log("[DEBUG] Using modern srcObject to set stream");
          video.srcObject = stream;
        } else {
          console.log("[DEBUG] Using legacy URL.createObjectURL for older browsers");
          video.src = window.URL.createObjectURL(stream); // for older browsers
        }

        this.isStreamStart = true;
        console.log("[DEBUG] Set isStreamStart to true");

        video
          .play()
          .then(() => {
            console.log("[DEBUG] Video playback started successfully");
          })
          .catch((err) => {
            console.error("[DEBUG] Error starting video playback:", err);
            console.log("[DEBUG] Attempting to play muted as fallback");
            video.muted = true;
            video
              .play()
              .then(() => console.log("[DEBUG] Muted video playback started"))
              .catch((e) => console.error("[DEBUG] Even muted playback failed:", e));
          });
      });

      peer.on("track", (track, stream) => {
        console.log("[DEBUG] Track received:", {
          kind: track.kind,
          enabled: track.enabled,
          readyState: track.readyState,
          id: track.id,
        });
      });

      peer.on("connect", () => {
        console.log("[DEBUG] Viewer Peer connected successfully");
        console.log("[DEBUG] WebRTC data channel established");
        // this.isStreamStart = true;
        // this.streamLoading = false;
      });

      peer.on("close", () => {
        console.log("[DEBUG] Viewer Peer connection closed");
        console.log("[DEBUG] WebRTC connection terminated");
        // peer.destroy();
        // cleanupCallback();
      });

      peer.on("error", (err) => {
        console.error("[DEBUG] WebRTC peer error:", err);
        console.log("[DEBUG] Error details:", {
          message: err.message,
          name: err.name,
          stack: err.stack,
        });
      });

      console.log("[DEBUG] Processing incoming offer:", incomingOffer);
      if (incomingOffer.type === "offer") {
        console.log("[DEBUG] Valid offer detected, updating SDP");
        const updatedOffer = {
          ...incomingOffer,
          sdp: `${incomingOffer.sdp}\n`,
        };

        console.log("[DEBUG] Signaling offer to peer:", updatedOffer.type);
        peer.signal(updatedOffer);
        console.log("[DEBUG] Offer signaled to peer");
      } else {
        console.error("[DEBUG] Invalid offer type:", incomingOffer.type);
      }
    },

    async initializeSignalOfferChannel() {
      console.log("[DEBUG] initializeSignalOfferChannel - Starting with authUserId:", this.authUserId);

      try {
        // Check if we have the required authentication
        if (!this.$store.state.apollo_token) {
          console.error("[DEBUG] Missing authentication token for signal offer channel");
          throw new Error("Authentication token required");
        }

        console.log("[DEBUG] Subscribing to private channel:", `stream-signal-channel.${this.authUserId}`);
        this.signalOfferChannel = window.Echo.private(`stream-signal-channel.${this.authUserId}`);

        console.log("[DEBUG] Adding event listener for StreamOffer");
        this.signalOfferChannel.listen(
          "StreamOffer",
          ({ data }) => {
            console.log("[DEBUG] StreamOffer event received:", data);

            if (data.offer && data.offer.type === "offer") {
              console.log("[DEBUG] Valid offer received from broadcaster:", data.broadcaster);
              console.log("[DEBUG] Offer SDP preview:", data.offer.sdp?.substring(0, 100) + "...");

              this.broadcasterId = data.broadcaster;
              console.log("[DEBUG] Set broadcasterId to:", this.broadcasterId);

              console.log("[DEBUG] Creating viewer peer with offer");
              this.createViewerPeer(data.offer, data.broadcaster);
            } else {
              console.error("[DEBUG] Invalid offer received:", data.offer);
            }
          }
        );

        console.log("[DEBUG] Successfully subscribed to StreamOffer events");
      } catch (error) {
        console.error("[DEBUG] Error in initializeSignalOfferChannel:", error);
        // Check if this is an authentication error
        if (error.status === 401 || (error.message && error.message.includes('401'))) {
          console.error("[DEBUG] Authentication error for signal offer channel");
          console.error("[DEBUG] Current auth state:", {
            token: this.$store.state.apollo_token ? "Present" : "Missing",
            user: this.$store.state.auth && this.$store.state.auth.user ? "Present" : "Missing",
            echoConnected: window.Echo && window.Echo.connector ? window.Echo.connector.pusher.connection.state : "Not initialized",
            socketId: window.Echo && window.Echo.socketId ? window.Echo.socketId() : "Unknown"
          });
        }
        throw error;
      }
    },

    async initializeSignalIceCadndidateOfferChannel() {
      console.log(
        "[DEBUG] initializeSignalIceCadndidateOfferChannel - Starting with authUserId:",
        this.authUserId
      );

      try {
        // Check if we have the required authentication
        if (!this.$store.state.apollo_token) {
          console.error("[DEBUG] Missing authentication token for ICE candidate channel");
          throw new Error("Authentication token required");
        }

        console.log(
          "[DEBUG] Subscribing to private channel for ICE candidates:",
          `stream-signal-channel.${this.authUserId}`
        );
        this.signalIceCandidateOfferChannel = window.Echo.private(`stream-signal-channel.${this.authUserId}`);

        console.log("[DEBUG] Adding event listener for StreamCandidateOffer");
        this.signalIceCandidateOfferChannel.listen(
          "StreamCandidateOffer",
          ({ data }) => {
            console.log("[DEBUG] StreamCandidateOffer event received:", data);
            console.log("[DEBUG] Current broadcasterPeer:", this.broadcasterPeer ? "exists" : "null");

            if (!this.broadcasterPeer) {
              console.error("[DEBUG] Cannot process ICE candidate - broadcasterPeer is null");
              return;
            }

            console.log("[DEBUG] Signaling ICE candidate to peer:", data.candidate);
            this.broadcasterPeer.signal(data.candidate);
            console.log("[DEBUG] ICE candidate signaled to peer");
          }
        );

        console.log("[DEBUG] Successfully subscribed to StreamCandidateOffer events");
      } catch (error) {
        console.error("[DEBUG] Error in initializeSignalIceCadndidateOfferChannel:", error);
        // Check if this is an authentication error
        if (error.status === 401 || (error.message && error.message.includes('401'))) {
          console.error("[DEBUG] Authentication error for ICE candidate channel");
          console.error("[DEBUG] Current auth state:", {
            token: this.$store.state.apollo_token ? "Present" : "Missing",
            user: this.$store.state.auth && this.$store.state.auth.user ? "Present" : "Missing",
            echoConnected: window.Echo && window.Echo.connector ? window.Echo.connector.pusher.connection.state : "Not initialized",
            socketId: window.Echo && window.Echo.socketId ? window.Echo.socketId() : "Unknown"
          });
        }
        throw error;
      }
    },

    removeBroadcastVideo() {
      console.log("[DEBUG] removeBroadcastVideo called");

      try {
        this.isStreamStart = false;
        console.log("[DEBUG] Set isStreamStart to false");

        this.streamLoading = false;
        console.log("[DEBUG] Set streamLoading to false");

        if (this.$refs.viewer && this.$refs.viewer.srcObject) {
          console.log("[DEBUG] Stopping all tracks in video srcObject");
          this.$refs.viewer.srcObject.getTracks().forEach((track) => track.stop());
          this.$refs.viewer.srcObject = null;
          console.log("[DEBUG] Cleared video srcObject");
        } else {
          console.log("[DEBUG] No video srcObject to clean up");
        }
      } catch (error) {
        console.error("[DEBUG] Error in removeBroadcastVideo:", error);
      }
    },
  },
  computed: {
    link() {
      return this.$route.params.link;
    },
    streamId() {
      return this.$route.query.streamId;
    },
    authUserId() {
      // Add null check to prevent errors when user is not authenticated
      return this.$store.state.auth && this.$store.state.auth.user ? this.$store.state.auth.user.id : null;
    },
  },
};
</script>

<style lang="stylus">
.stream-page {
  display: flex;
  height: 100%

  &__container {
    position: relative;
    width: 100%;
    display: flex
    gap: 20px;
    align-items: stretch;
    height: 100%;
    padding: 20px 0
    +below(1024px) {
      display block
    }
  }

  &__video {
    position relative
    width 100%
    height: 632px
    flex 77%
    +below(600px) {
      height: 500px;
    }

    video {
      width: 100%;
      height: 100%
      border-radius: 20px
      display: block;
      position: relative;
      object-fit: cover;
      outline: none
      +below(1024px) {
        border-radius 0
      }
    }
  }

  &__chat-wrapper {
    flex: 33%;
    position: relative;
    display: flex
    flex-direction: column
    padding-bottom: 20px;
    +below(1500px) {
      padding-right 0px
    }
    +below(1024px) {
      padding: 10px
      padding-bottom: 20px;
    }
    +below(600px) {
      margin-top 10px
      h3 {
        display none
      }
      padding-bottom: 20px;
      padding-right 0
      width: 100%;
      //position: absolute;
    }
  }

  &__chat {
    position: relative;
    display: block;
    height 100%
    overflow-y: auto;

    +below(1500px) {
      height 600px
    }

    +below(1100px) {
      height 550px
    }

    +below(919px) {
      height 500px
    }

    +below(600px) {
      height: 180px;
    }
  }

  &__chat-header {
    display flex
    justify-content space-between
    align-items center
  }

  &__chat-live {
    display flex
    flex-direction column
    gap 5px
    align-items center
    justify-content center
  }

  &__chat-head {
    display inline-flex
    justify-content center
    align-items center
    width 40px
    height 20px
    border-radius 6px
    padding 2px 5px
    font-weight 400
    font-size 0.5rem
    line-height 9px
    color: #EF7318
    box-shadow: 0 5px 10px -4px rgba(34, 60, 80, 0.2);
  }

  &__chat-count {
    display inline-flex
    justify-content center
    align-items center
    width 28px
    height 15px
    border-radius 6px
    color: #EF7318
    font-size 0.5rem
    box-shadow: 0 5px 10px -4px rgba(34, 60, 80, 0.2);
  }

  &__input {
    position relative
    width 100%

    +below(600px) {
      padding: 0 10px;
    }

    .input {
      width 100%
      height: 100%;
    }

    &-svg {
      width 24px
      height 24px
      position absolute
      top: 10px;
      right: 15px;
      z-index: 1;
      cursor pointer

      path {
        fill var(--blue-500)
      }
    }
  }

  &__btn.btn {
    position absolute
    top: 50%
    left: 50%
    transform: translate(-50%, -50%)
    z-index 10
    width 150px
  }
}
</style>
